/**
 * intakeTrService.ts
 *
 * The I/O behind the Title Search Intake Mode: create the search, upload the
 * recorded documents to its single container loan, submit them for processing,
 * and list the files of an existing search.
 *
 * Reuses the same calls as the legacy create modal and Document Processing step
 * (`workflowService.createJob`, S3 upload under the job's upload prefix,
 * `submitDocumentsForProcessing`, `submitJob`).
 *
 * Requirements: 6.6, 6.7.
 */

import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { fetchAuthSession } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { JobType, type WorkflowJob } from '../../graphql/types';
import * as mutations from '../../graphql/mutations';
import * as workflowService from '../../services/workflowService';
import { graphqlClient } from '../../services/graphqlClient';
import {
  buildCreateTRJobPayload,
  buildSubmitProcessingPayload,
} from '../../services/apiPayloadBuilder';
import { defaultTitleSearchJobName } from '../../titleSearch/createJob';
import { toTitleAnalysisInputs, getStoredFileStatus, isJobProcessing } from './intakeTrModel';
import type { IntakeFileStatus, TrIntakeForm } from './intakeTrModel';

/** A search that exists on the server but is not yet submitted. */
export interface CreatedSearch {
  job: WorkflowJob;
  /** The single container loan the recorded documents are filed under. */
  loanId: string;
  uploadBucket?: string;
}

export interface CreateSearchRequest {
  form: TrIntakeForm;
  /** Files still to upload; callbacks use indexes into this array. */
  files: File[];
  /** Pass the result of an earlier attempt to continue without a second search. */
  resume?: CreatedSearch;
  onCreated?: (created: CreatedSearch) => void;
  onFileStart?: (index: number) => void;
  onFileUploaded?: (index: number) => void;
  onFileFailed?: (index: number, message: string) => void;
}

export type CreateSearchFn = (request: CreateSearchRequest) => Promise<WorkflowJob>;

/** Thrown when one or more uploads failed; the search itself exists. */
export class UploadFailedError extends Error {
  constructor(public readonly failedCount: number) {
    super(`${failedCount} upload(s) failed`);
    this.name = 'UploadFailedError';
  }
}

async function getTenantId(): Promise<string> {
  const session = await fetchAuthSession();
  return (
    (session.tokens?.idToken?.payload['custom:tenantId'] as string | undefined) ||
    'default-tenant'
  );
}

async function resolveLoanId(jobId: string, tenantId: string): Promise<string> {
  const loans = await graphqlClient.queries.listLoansByJob(jobId, tenantId);
  const loanId = loans.items[0]?.loanId;
  if (!loanId) {
    throw new Error('The search has no document container');
  }
  return loanId;
}

async function uploadFiles(
  request: CreateSearchRequest,
  created: CreatedSearch
): Promise<void> {
  const bucket = created.uploadBucket || import.meta.env.VITE_S3_DOCUMENTS_BUCKET;
  if (!bucket) {
    request.files.forEach((_, i) => request.onFileFailed?.(i, 'Upload is not configured'));
    throw new UploadFailedError(request.files.length);
  }

  const session = await fetchAuthSession();
  const creds = session.credentials;
  if (!creds) {
    request.files.forEach((_, i) => request.onFileFailed?.(i, 'You are signed out'));
    throw new UploadFailedError(request.files.length);
  }

  // The job's upload location is the source of truth for the key prefix.
  const location = created.job.uploadLocation;
  const prefix = location
    ? location.replace(/^s3:\/\/[^/]+\//, '')
    : `default-tenant/unknown/${created.job.jobId}/`;

  const s3 = new S3Client({
    region: import.meta.env.VITE_AWS_REGION || 'us-west-2',
    credentials: {
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      sessionToken: creds.sessionToken,
    },
    requestChecksumCalculation: 'WHEN_REQUIRED',
    responseChecksumValidation: 'WHEN_REQUIRED',
  });

  let failed = 0;
  for (let i = 0; i < request.files.length; i++) {
    const file = request.files[i];
    request.onFileStart?.(i);
    try {
      await s3.send(
        new PutObjectCommand({
          Bucket: bucket,
          Key: `${prefix}${created.loanId}/${file.name}`,
          Body: new Uint8Array(await file.arrayBuffer()),
          ContentType: file.type,
        })
      );
      request.onFileUploaded?.(i);
    } catch (err) {
      failed += 1;
      request.onFileFailed?.(i, err instanceof Error ? err.message : 'Upload failed');
    }
  }
  if (failed > 0) throw new UploadFailedError(failed);
}

/**
 * Create the search, upload its documents and submit them for processing.
 * Resolves with the submitted job, ready to open in Review Mode.
 */
export const createTitleSearch: CreateSearchFn = async (request) => {
  const { form } = request;
  let created = request.resume;

  if (!created) {
    const order = form.order;
    const payload = buildCreateTRJobPayload(
      defaultTitleSearchJobName(order),
      JobType.TR,
      order.loanNumber.trim(),
      order.clientName.trim(),
      order.borrowerName.trim(),
      order.effectiveDate,
      order.propertyAddress.trim(),
      order.propertyCounty.trim(),
      order.propertyState.trim(),
      toTitleAnalysisInputs(form)
    );
    const response = await workflowService.createJob(payload);
    const tenantId = await getTenantId();
    created = {
      job: response.job,
      loanId: await resolveLoanId(response.job.jobId, tenantId),
      uploadBucket: response.uploadBucket,
    };
    request.onCreated?.(created);
  }

  if (request.files.length > 0) {
    await uploadFiles(request, created);
  }

  const tenantId = await getTenantId();
  const result = (await generateClient().graphql({
    query: mutations.submitDocumentsForProcessing,
    variables: { input: buildSubmitProcessingPayload(created.job.jobId, tenantId, JobType.TR) },
  })) as { data?: { submitDocumentsForProcessing?: WorkflowJob } };
  await graphqlClient.mutations.submitJob({ jobId: created.job.jobId });

  return result.data?.submitDocumentsForProcessing ?? created.job;
};

export interface StoredIntakeFile {
  id: string;
  name: string;
  size: number;
  status: IntakeFileStatus;
}

export type LoadFilesFn = (job: WorkflowJob) => Promise<StoredIntakeFile[]>;

/** Files stored on an existing search, with a per-file status. */
export const loadSearchFiles: LoadFilesFn = async (job) => {
  const tenantId = await getTenantId();
  const loans = await graphqlClient.queries.listLoansByJob(job.jobId, tenantId);
  const processing = isJobProcessing(job);
  const out: StoredIntakeFile[] = [];
  for (const loan of loans.items) {
    const files = await graphqlClient.queries.listFilesByLoan(loan.loanId, job.jobId, tenantId);
    for (const file of files.items) {
      out.push({
        id: file.fileId,
        name: file.filename,
        size: file.fileSize,
        status: getStoredFileStatus(file, processing),
      });
    }
  }
  return out;
};
